import { useMemo, useState } from "react";
import { Mail, Phone, ExternalLink, ChevronDown, AlertCircle, ShieldX, Send } from "lucide-react";
import { toast } from "sonner";
import { setUserApproval, updateOrderStatus, sendPaymentReminder } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";

const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-700",
  invoiced: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-700",
};

function daysOverdue(due) {
  if (!due) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return 0;
  const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function buildBuyerStats(orders) {
  const stats = {
    total_orders: orders.length,
    invoiced_total: 0,
    paid_total: 0,
    outstanding: 0,
    overdue_count: 0,
    has_overdue: false,
  };
  for (const o of orders) {
    const amt = Number(o.subtotal || 0);
    if (o.status === "invoiced") {
      stats.invoiced_total += amt;
      stats.outstanding += amt;
      if (daysOverdue(o.due_date) > 0) {
        stats.overdue_count += 1;
        stats.has_overdue = true;
      }
    } else if (o.status === "paid") {
      stats.paid_total += amt;
    }
  }
  return stats;
}

export default function BuyerAccounts({ buyers, orders, onRefresh }) {
  const [expandedId, setExpandedId] = useState(null);

  const ordersByUser = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const arr = map.get(o.user_id) || [];
      arr.push(o);
      map.set(o.user_id, arr);
    }
    return map;
  }, [orders]);

  if (!buyers || buyers.length === 0) {
    return <p className="text-sm text-muted-foreground py-12 text-center" data-testid="buyer-accounts-empty">No approved buyer accounts yet.</p>;
  }

  const revoke = async (u) => {
    if (!window.confirm(`Revoke wholesale access for ${u.business_name}?`)) return;
    try {
      await setUserApproval(u.id, false);
      toast.success("Approval revoked");
      onRefresh();
    } catch { toast.error("Could not revoke"); }
  };

  return (
    <div className="space-y-2" data-testid="buyer-accounts-list">
      {buyers.map((u) => {
        const userOrders = ordersByUser.get(u.id) || [];
        const stats = buildBuyerStats(userOrders);
        const isOpen = expandedId === u.id;
        return (
          <article
            key={u.id}
            data-testid={`buyer-account-${u.id}`}
            className={`rounded-2xl border transition-colors ${
              stats.has_overdue ? "border-rose-300 bg-rose-50/30" : "border-border bg-background"
            }`}
          >
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : u.id)}
              data-testid={`buyer-row-${u.id}`}
              className="w-full flex flex-wrap items-center gap-4 px-5 py-4 text-left"
            >
              <div className="flex-1 min-w-[220px]">
                <p className="font-medium text-foreground">{u.business_name || "—"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{u.contact_name || "—"} · {u.email}</p>
              </div>

              <div className="flex items-center gap-6 text-xs">
                <Metric label="Orders" value={stats.total_orders} />
                <Metric label="Paid" value={fmtCAD(stats.paid_total)} />
                <Metric label="Outstanding" value={fmtCAD(stats.outstanding)} accent={stats.outstanding > 0 ? "text-amber-700" : ""} />
              </div>

              {stats.has_overdue && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] uppercase tracking-[0.1em] font-medium" data-testid={`buyer-overdue-${u.id}`}>
                  <AlertCircle size={11} strokeWidth={2} /> {stats.overdue_count} overdue
                </span>
              )}

              <ChevronDown
                size={16}
                strokeWidth={1.5}
                className={`text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isOpen && (
              <div className="px-5 pb-5 border-t border-border/60 pt-5 space-y-6" data-testid={`buyer-detail-${u.id}`}>
                <section>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Account details</p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                    <Pair label="Email">
                      <a href={`mailto:${u.email}`} className="text-primary underline inline-flex items-center gap-1.5">
                        <Mail size={12} strokeWidth={1.6} /> {u.email}
                      </a>
                    </Pair>
                    <Pair label="Phone">
                      {u.phone ? (
                        <span className="inline-flex items-center gap-1.5"><Phone size={12} strokeWidth={1.6} />{u.phone}</span>
                      ) : "—"}
                    </Pair>
                    <Pair label="Website">
                      {u.website ? (
                        <a href={u.website} target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1.5 break-all">
                          <ExternalLink size={12} strokeWidth={1.6} /> {u.website.replace(/^https?:\/\//, "")}
                        </a>
                      ) : "—"}
                    </Pair>
                    <Pair label="Store type" wide>{u.store_classifications || "—"}</Pair>
                    <Pair label="Brands stocked" wide>{u.brands_stocked || "—"}</Pair>
                    {u.business_address && <Pair label="Address" wide>{u.business_address}</Pair>}
                    {u.tax_id && <Pair label="Tax ID">{u.tax_id}</Pair>}
                  </dl>
                  {u.message && (
                    <div className="mt-4 rounded-lg border-l-2 border-primary/40 bg-secondary/40 p-3.5">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Application message</p>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/85">{u.message}</p>
                    </div>
                  )}
                </section>

                <section>
                  <div className="flex items-baseline justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Invoice ledger</p>
                    {stats.has_overdue && (
                      <p className="text-[11px] text-rose-700 font-medium" data-testid={`buyer-overdue-warning-${u.id}`}>
                        {stats.overdue_count} unpaid invoice{stats.overdue_count > 1 ? "s" : ""} past due
                      </p>
                    )}
                  </div>
                  {userOrders.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4">No orders yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`buyer-orders-${u.id}`}>
                        <thead>
                          <tr className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">
                            <th className="text-left py-2 pr-3 font-normal">Order</th>
                            <th className="text-left py-2 pr-3 font-normal">Placed</th>
                            <th className="text-left py-2 pr-3 font-normal">Due</th>
                            <th className="text-right py-2 pr-3 font-normal">Total</th>
                            <th className="text-left py-2 pr-3 font-normal">Status</th>
                            <th className="text-right py-2 font-normal">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userOrders.map((o) => {
                            const od = daysOverdue(o.due_date);
                            const isOverdue = o.status === "invoiced" && od > 0;
                            return (
                              <tr key={o.id} className="border-b border-border/60 last:border-0" data-testid={`buyer-order-row-${o.id}`}>
                                <td className="py-2.5 pr-3 font-mono text-[11px]">#{o.id.slice(0, 8).toUpperCase()}</td>
                                <td className="py-2.5 pr-3 text-xs text-muted-foreground">{(o.created_at || "").slice(0, 10)}</td>
                                <td className={`py-2.5 pr-3 text-xs ${isOverdue ? "text-rose-700 font-medium" : "text-muted-foreground"}`}>
                                  {o.due_date ? o.due_date : "—"}
                                  {isOverdue && <span className="ml-1">· {od}d late</span>}
                                </td>
                                <td className="py-2.5 pr-3 text-right tabular-nums">{fmtCAD(o.subtotal)}</td>
                                <td className="py-2.5 pr-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${STATUS_STYLES[o.status] || "bg-secondary"}`}>{o.status}</span>
                                </td>
                                <td className="py-2.5 text-right">
                                  <div className="flex items-center justify-end gap-2 flex-wrap">
                                    {o.status === "pending" && (
                                      <StatusButton orderId={o.id} next="invoiced" label="Issue invoice" onDone={onRefresh} />
                                    )}
                                    {o.status === "invoiced" && (
                                      <>
                                        <ReminderButton order={o} isOverdue={isOverdue} onDone={onRefresh} />
                                        <StatusButton orderId={o.id} next="paid" label="Mark paid" onDone={onRefresh} />
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <div className="flex items-center justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => revoke(u)}
                    data-testid={`buyer-revoke-${u.id}`}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <ShieldX size={13} strokeWidth={1.6} /> Revoke wholesale access
                  </button>
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className={`tabular-nums font-medium text-foreground/90 mt-0.5 ${accent || ""}`}>{value}</p>
    </div>
  );
}

function Pair({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">{label}</dt>
      <dd className="text-foreground/90">{children}</dd>
    </div>
  );
}

function StatusButton({ orderId, next, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await updateOrderStatus(orderId, next);
      toast.success(label.replace("Issue ", "Issued ").replace("Mark ", "Marked "));
      onDone();
    } catch { toast.error("Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      className="px-3 py-1 rounded-full border border-border text-[11px] hover:bg-foreground/5 disabled:opacity-50"
    >
      {busy ? "…" : label}
    </button>
  );
}

function relativeTimeFromIso(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diffSec = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function ReminderButton({ order, isOverdue, onDone }) {
  const [busy, setBusy] = useState(false);
  const lastSent = order.last_reminder_sent_at;
  const count = order.reminder_count || 0;
  const handle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await sendPaymentReminder(order.id);
      toast.success("Payment reminder sent");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reminder");
    } finally {
      setBusy(false);
    }
  };
  const tooltip = lastSent
    ? `Last sent ${relativeTimeFromIso(lastSent)} · ${count} total`
    : "No reminders sent yet";
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      title={tooltip}
      data-testid={`send-reminder-${order.id}`}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-[11px] disabled:opacity-50 transition-colors ${
        isOverdue
          ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
          : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
      }`}
    >
      <Send size={11} strokeWidth={1.8} />
      {busy ? "Sending…" : lastSent ? `Remind again · ${relativeTimeFromIso(lastSent)}` : "Send reminder"}
    </button>
  );
}
